import { deleteObject, getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import { auth, storage } from '../config/firebase';
import { FirebaseUserContextService } from './FirebaseUserContextService';
import type { Client, User } from './UserService';

const MAX_MEDICAL_RECORD_FILE_SIZE_BYTES = 10 * 1024 * 1024;
const ALLOWED_MEDICAL_RECORD_FILE_TYPES = new Set([
  'application/pdf',
  'image/jpeg',
  'image/png',
]);

interface UploadMedicalRecordFileParams {
  clientId: string;
  patientId: string;
  patientUserId?: string | null;
  file: File;
  user: User;
  selectedClient: Client | null;
}

const sanitizeFileName = (fileName: string) =>
  fileName
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9._-]+/g, '-')
    .replace(/-+/g, '-');

const buildMedicalRecordFilePath = (
  clientId: string,
  patientId: string,
  fileName: string,
) => {
  const timestamp = Date.now();
  const randomSuffix = Math.random().toString(36).slice(2, 8);
  return `tenants/${clientId}/medical-records/${patientId}/${timestamp}-${randomSuffix}-${sanitizeFileName(fileName)}`;
};

const validateMedicalRecordFile = (file: File) => {
  if (!ALLOWED_MEDICAL_RECORD_FILE_TYPES.has(file.type)) {
    throw new Error('Tipo de arquivo inválido. Use PDF, JPG ou PNG.');
  }

  if (file.size > MAX_MEDICAL_RECORD_FILE_SIZE_BYTES) {
    throw new Error('O arquivo deve ter no máximo 10MB.');
  }
};

export class MedicalRecordFileStorageService {
  static async uploadFile(params: UploadMedicalRecordFileParams) {
    const { clientId, patientId, patientUserId, file, user, selectedClient } = params;

    if (!clientId) {
      throw new Error('Tenant não informado para o upload do documento.');
    }

    if (!patientId) {
      throw new Error('Paciente não informado para o upload do documento.');
    }

    validateMedicalRecordFile(file);
    await FirebaseUserContextService.syncAuthContext(user, selectedClient);

    const storagePath = buildMedicalRecordFilePath(clientId, patientId, file.name);
    const storageRef = ref(storage, storagePath);

    await uploadBytes(storageRef, file, {
      contentType: file.type,
      customMetadata: {
        clientId,
        tenantId: clientId,
        patientId,
        patientUserId: patientUserId ?? '',
        uploadedByUserId: user.userId,
        uploadedByFirebaseUid: user.firebase_uid || auth.currentUser?.uid || '',
        uploadedByRole: selectedClient?.userRole || user.userRole || '',
        originalName: file.name,
      },
    });

    const downloadUrl = await getDownloadURL(storageRef);

    return {
      provider: 'firebase-storage' as const,
      path: storagePath,
      downloadUrl,
      originalName: file.name,
      contentType: file.type,
    };
  }

  static async getDownloadUrl(path: string): Promise<string> {
    if (!path) {
      throw new Error('Documento sem caminho de armazenamento.');
    }

    return getDownloadURL(ref(storage, path));
  }

  static async deleteFile(path: string): Promise<void> {
    if (!path) {
      return;
    }

    await deleteObject(ref(storage, path));
  }
}
